import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { fmtDateTime } from '../../../../utils/ruDates';
import {
    JOB_ERROR_LABELS, JOB_STATE_LABELS, answerSupportQuestion, createSupportJob, loadSupportJobs, loadSupportTicket,
    loadSupportTickets, pauseSupportJob, readSupportTicket, refreshSupportTickets, resumeSupportJob, reviveSupportTicket, syncAllSupportTickets, updateSupportJob,
} from '../../../../services/supportAuto';
import { errorText, money } from '../feedback/feedbackUi';
import '../feedback/feedback.css';

const EMPTY_JOB = { goal: '', links: [], check_every_hours: 6, recreate_if_unresolved: false, max_messages_per_day: 10 };
const AUTHORS = { ozon_bot: 'Бот Ozon', ozon: 'Оператор Ozon', robot: 'Робот', seller: 'Вы' };

function stateChip(job) {
    if (!job) return null;
    const bad = job.state === 'failed' || job.state === 'paused';
    return <span className={`fb-chip${job.state === 'waiting_user' ? ' fb-chip--bad' : job.state === 'resolved' ? ' fb-chip--good' : bad ? '' : ''}`}>{JOB_STATE_LABELS[job.state] || job.state}</span>;
}

function JobEditor({ accountId, ticket, job, onSaved, onClose }) {
    const { request } = useApi();
    const [form, setForm] = useState(job
        ? { goal: job.goal, links: job.links || [], check_every_hours: job.check_every_hours, recreate_if_unresolved: job.recreate_if_unresolved, max_messages_per_day: job.max_messages_per_day }
        : EMPTY_JOB);
    const [message, setMessage] = useState('');

    const setLink = (index, field, value) => setForm({ ...form, links: form.links.map((link, position) => (position === index ? { ...link, [field]: value } : link)) });
    const save = async () => {
        setMessage('');
        const payload = {
            ...form,
            check_every_hours: Number(form.check_every_hours),
            max_messages_per_day: Number(form.max_messages_per_day),
            links: form.links.filter((link) => (link.url || '').trim()),
        };
        try {
            if (job) await updateSupportJob({ request, jobId: job.id, job: payload });
            else await createSupportJob({ request, accountId, job: { ...payload, ticket_id: ticket ? ticket.id : null } });
            onSaved();
        } catch (error) {
            setMessage(errorText(error));
        }
    };

    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-row fb-spread">
                <h4 style={{ margin: 0 }}>
                    {job ? 'Задание роботу' : ticket ? `Задание по обращению №${ticket.ticket_no}` : 'Новое задание (будет создано новое обращение)'}
                </h4>
                <button type="button" onClick={onClose}>Закрыть</button>
            </div>
            <label>
                <div className="fb-muted" style={{ marginBottom: 4 }}>Что нужно добиться от поддержки (в свободной форме)</div>
                <textarea className="fb-textarea" rows={5} maxLength={4000} value={form.goal} onChange={(event) => setForm({ ...form, goal: event.target.value })}
                    placeholder="Например: убрать блокировку карточки артикула 12345, подтвердив состав документами, ссылки на которые ниже." />
            </label>
            <div className="fb-faq">
                <div className="fb-muted">Материалы: ссылки на скриншоты, таблицы, исходники и описание, что в них. Робот сам страницы не открывает, а ссылается на них в обращении.</div>
                {form.links.map((link, index) => (
                    <div className="fb-faq__row" key={index}>
                        <input className="fb-input" placeholder="Ссылка" value={link.url || ''} onChange={(event) => setLink(index, 'url', event.target.value)} />
                        <input className="fb-input" placeholder="Что здесь" value={link.note || ''} onChange={(event) => setLink(index, 'note', event.target.value)} />
                        <input className="fb-input" style={{ maxWidth: 130 }} placeholder="SKU (если есть)" value={link.sku || ''} onChange={(event) => setLink(index, 'sku', event.target.value)} />
                        <button type="button" onClick={() => setForm({ ...form, links: form.links.filter((_, position) => position !== index) })}>Убрать</button>
                    </div>
                ))}
                <div><button type="button" onClick={() => setForm({ ...form, links: [...form.links, { url: '', note: '', sku: '' }] })}>Добавить ссылку</button></div>
            </div>
            <div className="fb-row">
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="fb-muted">Проверять раз в, часов</span>
                    <input className="fb-input" type="number" min="1" max="168" value={form.check_every_hours} onChange={(event) => setForm({ ...form, check_every_hours: event.target.value })} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span className="fb-muted">Не больше сообщений в сутки</span>
                    <input className="fb-input" type="number" min="1" max="100" value={form.max_messages_per_day} onChange={(event) => setForm({ ...form, max_messages_per_day: event.target.value })} />
                </label>
            </div>
            <label><input type="checkbox" checked={form.recreate_if_unresolved} onChange={(event) => setForm({ ...form, recreate_if_unresolved: event.target.checked })} /> Создать новое обращение, если задача не решилась</label>
            <div className="fb-note">Робот пишет в обращение сам. Каждая проверка оплачивается с баланса плательщика по токенам, как ответы на отзывы; расход по заданию виден в карточке.</div>
            <div className="fb-actions">
                <button type="button" className="primary" disabled={form.goal.trim().length < 3} onClick={save}>{job ? 'Сохранить' : 'Запустить робота'}</button>
                {message && <span className="fb-note">{message}</span>}
            </div>
        </div>
    );
}

function Questions({ job, onChanged }) {
    const { request } = useApi();
    const [answers, setAnswers] = useState({});
    const open = (job.questions || []).filter((question) => !question.answer);
    if (!open.length) return null;
    const send = async (question) => {
        try {
            await answerSupportQuestion({ request, jobId: job.id, questionId: question.id, answer: answers[question.id] || '' });
            onChanged();
        } catch (error) {
            window.alert(errorText(error));
        }
    };
    return (
        <div className="fb-card fb-chip--bad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <b>Роботу не хватает сведений</b>
            {open.map((question) => (
                <div key={question.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div>{question.question}</div>
                    <div className="fb-row">
                        <input className="fb-input" style={{ flex: 1 }} placeholder="Ваш ответ" value={answers[question.id] || ''}
                            onChange={(event) => setAnswers({ ...answers, [question.id]: event.target.value })} />
                        <button type="button" className="primary" disabled={!(answers[question.id] || '').trim()} onClick={() => send(question)}>Ответить</button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function JobSummary({ job, onChanged, onEdit }) {
    const { request } = useApi();
    const toggle = async () => {
        try {
            if (job.state === 'paused') await resumeSupportJob({ request, jobId: job.id });
            else await pauseSupportJob({ request, jobId: job.id });
            onChanged();
        } catch (error) {
            window.alert(errorText(error));
        }
    };
    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="fb-row fb-spread">
                <div className="fb-row">{stateChip(job)}<span className="fb-muted">проверка раз в {job.check_every_hours} ч, сообщений сегодня {job.messages_today}/{job.max_messages_per_day}</span></div>
                <div className="fb-row">
                    <button type="button" onClick={onEdit}>Изменить</button>
                    {['active', 'paused'].includes(job.state) && <button type="button" onClick={toggle}>{job.state === 'paused' ? 'Возобновить' : 'Пауза'}</button>}
                </div>
            </div>
            <div className="fb-item__text">{job.goal}</div>
            {job.last_summary && <div className="fb-note">Итог робота: {job.last_summary}</div>}
            {job.last_error && <div className="fb-note">{JOB_ERROR_LABELS[job.last_error] || job.last_error}</div>}
            <div className="fb-note">
                Потрачено {money(job.ai_cost_rub)} на {job.ai_calls} проверок{job.avg_cost_rub !== null ? `, в среднем ${money(job.avg_cost_rub)} за проверку` : ''}.
                {job.next_check_at && job.state === 'active' ? ` Следующая проверка: ${fmtDateTime(job.next_check_at)}.` : ''}
            </div>
            <Questions job={job} onChanged={onChanged} />
        </div>
    );
}

function TicketDetail({ ticketId, accountId, onChanged, onClose }) {
    const { request } = useApi();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null);
    const [reading, setReading] = useState(false);

    const reload = useCallback(async () => {
        try {
            setData(await loadSupportTicket({ request, ticketId }));
        } catch (err) {
            setError(errorText(err));
        }
    }, [request, ticketId]);
    useEffect(() => { setData(null); reload(); }, [reload]);

    if (error) return <div className="fb-card fb-chip--bad">{error}</div>;
    if (!data) return <div className="fb-card"><span className="fb-muted">Загрузка…</span></div>;
    const changed = () => { reload(); onChanged(); };
    const readNow = async () => {
        setReading(true);
        try {
            await readSupportTicket({ request, ticketId });
            // Агент читает обращение в течение нескольких минут: обновляем карточку, пока не появится переписка.
            for (let attempt = 0; attempt < 30; attempt += 1) {
                await new Promise((resolve) => setTimeout(resolve, 8000));
                const fresh = await loadSupportTicket({ request, ticketId });
                setData(fresh);
                if (fresh.messages.length) break;
            }
        } catch (err) {
            setError(errorText(err));
        } finally {
            setReading(false);
        }
    };
    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-row fb-spread">
                <div>
                    <h4 style={{ margin: 0 }}>{data.title || 'Обращение'} №{data.ticket_no}</h4>
                    <div className="fb-muted">{data.ozon_status || data.status}</div>
                </div>
                <button type="button" onClick={onClose}>Закрыть</button>
            </div>
            {data.jobs.map((job) => <JobSummary key={job.id} job={job} onChanged={changed} onEdit={() => setEditing(job)} />)}
            {data.status === 'active' && !data.jobs.length && !editing && (
                <div><button type="button" className="primary" onClick={() => setEditing({})}>Поставить задание роботу</button></div>
            )}
            {editing && (
                <JobEditor accountId={accountId} ticket={data} job={editing.id ? editing : null}
                    onSaved={() => { setEditing(null); changed(); }} onClose={() => setEditing(null)} />
            )}
            <div className="fb-faq">
                <div className="fb-muted">Переписка</div>
                {data.messages.length === 0 && (
                    <div className="fb-row">
                        <span className="fb-note">Переписка ещё не прочитана агентом.</span>
                        <button type="button" disabled={reading} onClick={readNow}>{reading ? 'Читаем…' : 'Прочитать переписку'}</button>
                    </div>
                )}
                {data.messages.map((message) => (
                    <div key={message.id} className="fb-item__text" style={{ opacity: message.status === 'queued' ? 0.6 : 1 }}>
                        <b>{AUTHORS[message.author] || message.author}</b>
                        {message.status === 'queued' ? ' (отправляется)' : message.status === 'failed' ? ' (не отправлено)' : ''}
                        <div style={{ whiteSpace: 'pre-wrap' }}>{message.text}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function SupportAuto({ accountId }) {
    const { request } = useApi();
    const [view, setView] = useState('active');
    const [tickets, setTickets] = useState({ total: 0, items: [] });
    const [jobs, setJobs] = useState([]);
    const [selected, setSelected] = useState('');
    const [editing, setEditing] = useState(null);
    const [editJob, setEditJob] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [syncSince, setSyncSince] = useState('');
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        if (!accountId) return;
        setLoading(true);
        setError('');
        try {
            if (view === 'jobs') setJobs((await loadSupportJobs({ request, accountId })).items);
            else setTickets(await loadSupportTickets({ request, accountId, state: view }));
        } catch (err) {
            setError(errorText(err));
        } finally {
            setLoading(false);
        }
    }, [request, accountId, view]);
    useEffect(() => { setSelected(''); setEditing(null); setEditJob(null); reload(); }, [reload]);

    if (!accountId) return <p className="muted">Выберите кабинет.</p>;

    const refresh = async () => {
        setNotice('');
        try {
            const result = await refreshSupportTickets({ request, accountId });
            setNotice(result.scheduled ? 'Агент обновит список обращений в ближайшие минуты' : 'Обновление уже идёт');
        } catch (err) {
            setNotice(errorText(err));
        }
    };
    const syncAll = async () => {
        setNotice('');
        try {
            const result = await syncAllSupportTickets({ request, accountId, since: syncSince || undefined });
            setNotice(result.scheduled
                ? 'Агент прочитает полный список обращений, затем переписку каждого. Это займёт время, список пополняется по мере чтения.'
                : 'Загрузка списка уже идёт');
        } catch (err) {
            setNotice(errorText(err));
        }
    };
    const revive = async (ticket) => {
        try {
            await reviveSupportTicket({ request, ticketId: ticket.id });
            setView('jobs');
        } catch (err) {
            setNotice(errorText(err));
        }
    };

    return (
        <div className="fb" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-card">
                <div className="fb-row fb-spread">
                    <div>
                        <h4 style={{ margin: 0 }}>Авто Техподдержка</h4>
                        <div className="fb-muted">
                            Поставьте роботу задание, и он будет переписываться с поддержкой Ozon в обращении: уточнять, прикладывать ваши материалы,
                            спрашивать у вас, чего не хватает, и при необходимости заводить новое обращение. Работает, пока включён локальный агент.
                        </div>
                    </div>
                    <div className="fb-row">
                        <button type="button" className="primary" onClick={() => { setSelected(''); setEditing({}); }}>Новое задание</button>
                        <button type="button" onClick={refresh}>Обновить список</button>
                        <button type="button" onClick={syncAll} title="Читает список обращений (и закрытые) и переписку каждого, у которого её ещё нет. Дату можно не указывать: тогда загрузятся все обращения">Загрузить все обращения с даты</button>
                        <input className="fb-input" type="date" value={syncSince} onChange={(event) => setSyncSince(event.target.value)} title="Не читать обращения с последней активностью раньше этой даты; пусто — все" />
                    </div>
                </div>
                {notice && <div className="fb-note" style={{ marginTop: 8 }}>{notice}</div>}
            </div>
            <div className="account-tabs account-tabs--secondary">
                {[['active', 'Активные'], ['closed', 'Закрытые'], ['jobs', 'Задания']].map(([id, label]) => (
                    <button key={id} type="button" className={view === id ? 'active' : ''} onClick={() => setView(id)}>{label}</button>
                ))}
            </div>
            {error && <div className="fb-card fb-chip--bad">{error}</div>}
            {editing && <JobEditor accountId={accountId} ticket={null} job={null} onSaved={() => { setEditing(null); setView('jobs'); reload(); }} onClose={() => setEditing(null)} />}

            {editJob && <JobEditor accountId={accountId} ticket={null} job={editJob} onSaved={() => { setEditJob(null); reload(); }} onClose={() => setEditJob(null)} />}

            {view === 'jobs' ? (
                <div className="fb-list">
                    {!loading && jobs.length === 0 && <div className="fb-card fb-empty">Заданий пока нет.</div>}
                    {jobs.map((job) => (
                        <div key={job.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div className="fb-muted">{job.ticket ? `Обращение №${job.ticket.ticket_no}` : 'Новое обращение будет создано роботом'}</div>
                            <JobSummary job={job} onChanged={reload} onEdit={() => setEditJob(job)} />
                        </div>
                    ))}
                </div>
            ) : (
                <div className={selected ? 'fb-two' : ''}>
                    <div className="fb-table-wrap">
                        <table className="fb-table">
                            <thead><tr><th>№</th><th>Тема</th><th>Статус Ozon</th><th>Последнее</th><th>Задание</th><th /></tr></thead>
                            <tbody>
                                {tickets.items.map((ticket) => (
                                    <tr key={ticket.id} className="fb-clickable" onClick={() => setSelected(ticket.id)}>
                                        <td>{ticket.ticket_no}</td>
                                        <td style={{ maxWidth: 260 }}>{ticket.title || '—'}</td>
                                        <td>{ticket.ozon_status || ticket.status}</td>
                                        <td style={{ maxWidth: 320 }}>{ticket.last_snippet ? ticket.last_snippet.slice(0, 120) : '—'}</td>
                                        <td>{ticket.job ? stateChip(ticket.job) : <span className="fb-muted">нет</span>}</td>
                                        <td>
                                            {ticket.status === 'closed' && (
                                                <button type="button" onClick={(event) => { event.stopPropagation(); revive(ticket); }}>Реанимировать</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!loading && tickets.items.length === 0 && (
                            <div className="fb-empty">Обращений нет. Нажмите «Обновить список»: агент прочитает обращения кабинета в Ozon.</div>
                        )}
                    </div>
                    {selected && <TicketDetail ticketId={selected} accountId={accountId} onChanged={reload} onClose={() => setSelected('')} />}
                </div>
            )}
            {tickets.total > tickets.items.length && view !== 'jobs' && <div className="fb-note">Показано {tickets.items.length} из {tickets.total}.</div>}
        </div>
    );
}
